import {
  forwardRef,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useState,
} from 'react';

import { Field, FieldProps } from 'react-final-form';

import { Select, SelectProps } from '@cast/design-system';

import { useControlsGroupState } from './controls-state';
import { useRffFormState } from '../hooks';
import { showErrorOnChange } from '../utils';

type RffSelectProps<
  OptionItem = unknown,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
> = SelectProps<OptionItem, OptionValue, SelectionType> & {
  name: string;
  label?: ReactNode;
  renderError?: (error: string | string[]) => ReactNode;
  fieldProps?: Partial<FieldProps<OptionItem, any>>;
  onMouseOver?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  success?: boolean;
};

export const RffSelect = forwardRef<HTMLInputElement, RffSelectProps>(
  ({ renderError, fieldProps, label, success, ...autocompleteProps }, ref) => {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const { mode } = useRffFormState();
    const { disabled, readOnly } = useControlsGroupState({
      disabled: autocompleteProps.disabled,
      readOnly: mode === 'view' || autocompleteProps.inputProps?.readOnly,
    });

    return (
      <Field
        {...fieldProps}
        name={autocompleteProps.name}
        render={(props) => {
          const {
            input: { onBlur, onFocus, onChange, value },
            meta,
          } = props;
          const showError = showErrorOnChange(meta) && !popoverOpen;
          return (
            <Select
              {...autocompleteProps}
              value={autocompleteProps.value || value || null}
              onChange={(option: any, val: any) => {
                autocompleteProps.onChange?.(option, val);
                onChange(val);
                setPopoverOpen(false);
              }}
              onOpen={() => {
                setPopoverOpen(true);
                autocompleteProps.onOpen?.();
              }}
              onClose={() => {
                setPopoverOpen(false);
                autocompleteProps.onClose?.();
              }}
              inputProps={{
                label,
                onBlur,
                onFocus,
                error: showError
                  ? renderError?.(meta.error) || meta.error
                  : undefined,
                success: success ?? (meta.touched && meta.valid),
                // Tooltip passes these for it's direct child
                onMouseOver: autocompleteProps.onMouseOver,
                onMouseLeave: autocompleteProps.onMouseLeave,
                ...autocompleteProps.inputProps,
                readOnly,
              }}
              disabled={disabled}
              ref={ref}
            />
          );
        }}
      />
    );
  }
) as (<
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
>(
  props: RffSelectProps<OptionItem, OptionValue, SelectionType> & {
    ref?: React.Ref<HTMLInputElement>;
  }
) => ReactElement) & {
  displayName: string;
};

RffSelect.displayName = 'RffSelect';
